// frontend/src/pages/admin/lcl/tabs/HistoryTab.jsx
import { useState, useMemo } from 'react';
import axios from 'axios';
import { FiDownload, FiRefreshCw } from 'react-icons/fi';
import toast from 'react-hot-toast';
import { supabase } from '../../../../lib/supabaseClient';
import { useLclBatches } from '../hooks/useLclBatches';
import PeriodFilter, { inPeriod } from '../components/PeriodFilter';

const fmtDateTime = (d) => (d ? new Date(d).toLocaleString('fr-FR') : '—');

async function downloadFile(id, name) {
  const t = toast.loading('Téléchargement…');
  try {
    const { data: { session } } = await supabase.auth.getSession();
    const resp = await axios.get(`/api/lcl/batches/${id}/download`, {
      responseType: 'blob',
      headers: { Authorization: `Bearer ${session?.access_token}` },
    });
    const url = URL.createObjectURL(resp.data);
    const a = Object.assign(document.createElement('a'), { href: url, download: name });
    a.click(); URL.revokeObjectURL(url);
    toast.success('OK', { id: t });
  } catch (e) {
    toast.error(`Erreur: ${e.response?.data?.message || e.message}`, { id: t });
  }
}

export default function HistoryTab() {
  const [type, setType] = useState('');
  const [period, setPeriod] = useState({ mode: 'all' });
  const { data, loading, refetch } = useLclBatches({ type });

  const filtered = useMemo(
    () => data.filter((b) => inPeriod(b.generated_at, period)),
    [data, period]
  );

  return (
    <div className="space-y-3">

      {/* Filtres */}
      <div className="bg-white rounded border border-gray-200 p-2 flex flex-wrap items-center gap-2">

        <div className="inline-flex rounded border border-gray-200 overflow-hidden">
          {[
            { k: '',                    l: 'Tous' },
            { k: 'mandate_csv',         l: 'Mandats CSV' },
            { k: 'prelevement_csv',     l: 'Prélèvements CSV' },
            { k: 'prelevement_cfonb',   l: 'Prélèvements CFONB (legacy)' },
          ].map(({ k, l }) => (
            <button
              key={k}
              onClick={() => setType(k)}
              className={`px-2.5 py-1 text-[11px] font-medium transition-colors ${
                type === k ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'
              }`}
            >
              {l}
            </button>
          ))}
        </div>

        <PeriodFilter period={period} setPeriod={setPeriod} />

        <button
          onClick={refetch}
          className="inline-flex items-center gap-1 px-2 py-1 text-[11px] rounded border border-gray-200 hover:bg-gray-50 text-gray-600"
        >
          <FiRefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
          Actualiser
        </button>

        <div className="text-[10px] text-gray-500 ml-auto">
          {filtered.length} export{filtered.length > 1 ? 's' : ''}
        </div>
      </div>

      {/* Tableau */}
      <div className="bg-white rounded shadow border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-8 gap-2 text-xs text-gray-400">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500" />
            Chargement…
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-8 text-xs text-gray-400">
            Aucun export pour cette sélection.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-100">
              <thead className="bg-gray-50">
                <tr>
                  {['Date', 'Type', 'Fichier', 'Items', 'ICS', 'Action']
                    .map((h) => (
                      <th key={h}
                          className="px-3 py-2 text-left text-[10px] font-semibold
                                     text-gray-500 uppercase tracking-wider whitespace-nowrap">
                        {h}
                      </th>
                    ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filtered.map((b) => {
                  const isMandate = b.type === 'mandate_csv';
                  const isCsvPrelev = b.type === 'prelevement_csv';
                  const typeLabel = isMandate ? 'Mandats CSV' : isCsvPrelev ? 'Prélèv. CSV' : 'Prélèv. CFONB';
                  const typeClass = isMandate
                    ? 'bg-blue-100 text-blue-800'
                    : isCsvPrelev
                      ? 'bg-green-100 text-green-800'
                      : 'bg-purple-100 text-purple-800';
                  return (
                    <tr key={b.id} className="hover:bg-gray-50">
                      <td className="px-3 py-2 whitespace-nowrap text-[11px] text-gray-500">
                        {fmtDateTime(b.generated_at)}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold ${typeClass}`}>
                          {typeLabel}
                        </span>
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-[11px] text-gray-700 font-mono">
                        {b.file_name}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-[11px] font-semibold text-gray-800">
                        {b.item_count}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-[10px] text-gray-500 font-mono">
                        {b.ics_used || '—'}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        {b.storage_path ? (
                          <button
                            onClick={() => downloadFile(b.id, b.file_name)}
                            className="inline-flex items-center gap-1 px-2 py-1 text-[11px] rounded
                                       border border-gray-200 hover:bg-gray-50 text-gray-600"
                          >
                            <FiDownload className="h-3 w-3" />
                            Télécharger
                          </button>
                        ) : (
                          <span className="text-[10px] text-gray-400">Non archivé</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

    </div>
  );
}
